// =============================================================
// PBP Guinée — Client Axios centralisé
// =============================================================

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// ── Intercepteur requêtes (injection JWT) ───────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('pbp_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Intercepteur réponses (refresh automatique) ─────────────
let isRefreshing = false;
let failedQueue: Array<{ resolve: Function; reject: Function }> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('pbp_refresh_token');

      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        localStorage.setItem('pbp_access_token', access);
        processQueue(null, access);

        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${access}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Affichage des erreurs — toast ID fixe pour éviter les doublons sur retry
    if (error.response?.status === 403) {
      toast.error('Accès refusé. Permissions insuffisantes.', { id: 'err-403' });
    } else if (error.response?.status === 429) {
      toast.error('Trop de requêtes. Veuillez patienter.', { id: 'err-429' });
    } else if (error.response?.status && error.response.status >= 500) {
      toast.error('Erreur serveur. Contactez l\'administrateur.', { id: 'err-500' });
    }

    return Promise.reject(error);
  },
);

const redirectToLogin = () => {
  localStorage.removeItem('pbp_access_token');
  localStorage.removeItem('pbp_refresh_token');
  localStorage.removeItem('pbp_user');
  window.location.href = '/login';
};

export default api;
